'use client';

import { c as _c } from "react/compiler-runtime";
import { PAGE_BAR_ROW_CLASS, SetPageBar } from '@/components/header/page-bar';
import { PersonAvatar } from '@/components/ui/person-avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { HofContact } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { translate } from '@/features/hof-dashboard/texts';
import { signOutNeedsConnectionText, useSignOut } from '@/hooks/use-sign-out';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { reloadPage } from '@/utils/reload-page';
import { ChevronRight, LogOut } from 'lucide-react';
import type React from 'react';
import { Fragment, useId, useState } from 'react';

/** One step of the path: a way back, or where the user is, which may be a choice of Höfe. */
export interface Crumb {
  label: string;
  onClick?: () => void;
  /** Replaces the label on the last step, such as the switch between a person's Höfe. */
  control?: React.ReactNode;
}
const CRUMB_LINK_CLASS = 'inline-flex min-h-11 cursor-pointer items-center rounded-lg px-1 text-green-700 underline-offset-2 hover:text-green-900 hover:underline focus-visible:outline-2 focus-visible:outline-green-600';

/** Where the user is in the dashboard, each step before the last one a way back. */
const Breadcrumbs: React.FC<{
  crumbs: Crumb[];
  locale: Locale;
}> = t0 => {
  const $ = _c(11);
  const {
    crumbs,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate("breadcrumbs", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== crumbs) {
    let t3;
    if ($[4] !== crumbs.length) {
      t3 = (crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return <Fragment key={crumb.label}>{index > 0 && <li aria-hidden={true} className="text-green-500"><ChevronRight className="h-4 w-4" /></li>}<li className="min-w-0">{isLast ? crumb.control ?? <span aria-current="page" className="text-heading px-1 font-semibold">{crumb.label}</span> : <button type="button" className={CRUMB_LINK_CLASS} onClick={crumb.onClick}>{crumb.label}</button>}</li></Fragment>;
      };
      $[4] = crumbs.length;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = crumbs.map(t3);
    $[2] = crumbs;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <ol className="flex flex-wrap items-center gap-x-1 text-sm">{t2}</ol>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <nav aria-label={t1} className="min-w-0">{t3}</nav>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};

/** The last step of the path as a choice between Höfe, for those who see more than one. */
export const HofSwitch: React.FC<{
  hoefe: {
    id: string;
    name: string;
  }[];
  value: string;
  onChange: (hofId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(20);
  const {
    hoefe,
    value,
    onChange,
    locale
  } = t0;
  const id = useId();
  let t1;
  if ($[0] !== locale) {
    t1 = translate("hof", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== id || $[3] !== t1) {
    t2 = <label htmlFor={id} className="sr-only">{t1}</label>;
    $[2] = id;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectValue />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== id) {
    t4 = <SelectTrigger id={id} aria-current="page" className="border-line text-heading h-11 w-auto max-w-full min-w-0 gap-2 rounded-lg bg-white font-semibold shadow-none focus-visible:ring-2 focus-visible:ring-green-600 sm:max-w-[24rem] [&>span]:block [&>span]:truncate">{t3}</SelectTrigger>;
    $[6] = id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== hoefe) {
    t5 = hoefe.map(_temp);
    $[8] = hoefe;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <SelectContent className="max-w-(--radix-select-content-available-width) bg-white">{t5}</SelectContent>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onChange || $[13] !== t4 || $[14] !== t6 || $[15] !== value) {
    t7 = <Select value={value} onValueChange={onChange}>{t4}{t6}</Select>;
    $[12] = onChange;
    $[13] = t4;
    $[14] = t6;
    $[15] = value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <>{t2}{t7}</>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};

/**
 * Who is signed in, and the way out: the dashboard shows one person's Höfe, so on a shared
 * phone the next person needs to see whose they are and switch. They are shown as in the chat,
 * by their picture or their initials, which leaves the path the room; the name is the
 * avatar's tooltip and part of the button's label.
 */
export const AccountButton: React.FC<{
  userId: string;
  name: string;
  locale: Locale;
}> = t0 => {
  const $ = _c(23);
  const {
    userId,
    name,
    locale
  } = t0;
  const signOut = useSignOut();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      staleTime: 300000
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const picture = trpc.profilePicture.getProfilePicture.useQuery(undefined, t1);
  const [failed, setFailed] = useState(false);
  const label = `${translate("signedInAs", locale, {
    name
  })} · ${translate("signOut", locale)}`;
  const t2 = picture.data?.pictureUrl;
  let t3;
  if ($[1] !== name || $[2] !== t2 || $[3] !== userId) {
    t3 = <PersonAvatar seed={userId} name={name} pictureUrl={t2} className="h-9 w-9 text-xs" />;
    $[1] = name;
    $[2] = t2;
    $[3] = userId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== name || $[6] !== t3) {
    t4 = <span title={name} className="flex">{t3}</span>;
    $[5] = name;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== signOut) {
    t5 = () => {
      setFailed(false);
      signOut().then(reloadPage).catch(error => {
        console.warn("Signing out of the Hof dashboard failed", error);
        setFailed(true);
      });
    };
    $[8] = signOut;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LogOut className="h-4 w-4" aria-hidden={true} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== label || $[12] !== t5) {
    t7 = <button type="button" aria-label={label} title={label} className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-green-700 transition hover:bg-gray-100 hover:text-green-900 focus-visible:outline-2 focus-visible:outline-green-600" onClick={t5}>{t6}</button>;
    $[11] = label;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t7) {
    t8 = <div className="flex items-center gap-1">{t4}{t7}</div>;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== failed || $[18] !== locale) {
    t9 = failed && <p role="alert" className="text-danger-text text-[13px] leading-[18px] font-medium">{signOutNeedsConnectionText[locale]}</p>;
    $[17] = failed;
    $[18] = locale;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div className="flex flex-col items-end gap-1">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};

/** The Hof's responsible people (AVP) in one line under its name, each a link to write them. */
export const ResponsibleLine: React.FC<{
  people: HofContact[];
  locale: Locale;
}> = t0 => {
  const $ = _c(10);
  const {
    people,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate("responsibleShort", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="font-semibold">{t1}: </span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== locale || $[5] !== people) {
    t3 = people.length === 0 ? translate("responsibleMissing", locale) : people.map(_temp2);
    $[4] = locale;
    $[5] = people;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <p className="text-[15px] text-green-700">{t2}{t3}</p>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

/**
 * The head of every screen. The path, with the account on the right, goes into the bar that
 * sticks below the site's header, above the page's title and whatever an editor placed before
 * the dashboard. The path names where the user is, so the screen repeats it in no heading of its
 * own; what is left here is a line about it, such as the Hof's AVP.
 */
export const DashboardHeader: React.FC<{
  crumbs: Crumb[];
  isReviewer: boolean;
  account: React.ReactNode;
  subline?: React.ReactNode;
  locale: Locale;
}> = t0 => {
  const $ = _c(15);
  const {
    crumbs,
    isReviewer,
    account,
    subline,
    locale
  } = t0;
  let t1;
  if ($[0] !== crumbs || $[1] !== locale) {
    t1 = <Breadcrumbs crumbs={crumbs} locale={locale} />;
    $[0] = crumbs;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isReviewer || $[4] !== locale) {
    t2 = isReviewer && <span className="border-line hidden rounded-full border bg-white px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-green-700 lg:inline">{translate("reviewerView", locale)}</span>;
    $[3] = isReviewer;
    $[4] = locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== account || $[7] !== t2) {
    t3 = <div className="flex shrink-0 items-center gap-2">{t2}{account}</div>;
    $[6] = account;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <SetPageBar><div className={PAGE_BAR_ROW_CLASS}>{t1}{t3}</div></SetPageBar>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== subline || $[13] !== t4) {
    t5 = <>{t4}{subline}</>;
    $[12] = subline;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
function _temp(candidate) {
  return <SelectItem key={candidate.id} value={candidate.id} className="min-h-11 whitespace-normal">{candidate.name}</SelectItem>;
}
function _temp2(person, index) {
  return <Fragment key={person.email}>{index > 0 && ", "}<a href={`mailto:${person.email}`} title={person.email} className="text-conveniat-green underline-offset-2 hover:underline">{person.name ?? person.email}</a></Fragment>;
}